import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  TenantDetailPayload,
  TenantOnboardingApiService,
  type CatalogRow,
} from 'src/app/core/services/tenant-onboarding-api.service';
import { AgentApiService, type AgentRecord } from 'src/app/core/services/agent-api.service';
import { BillingApiService } from 'src/app/core/services/billing-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import { TenantPhonePipe } from 'src/app/shared/pipes/tenant-phone.pipe';
import { AuditHistoryComponent } from 'src/app/shared/audit-history/audit-history.component';

@Component({
  selector: 'app-client-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    AppDatePipe,
    TenantPhonePipe,
    AuditHistoryComponent,
  ],
  templateUrl: './client-detail.component.html',
  styleUrl: './client-detail.component.css',
})
export class ClientDetailComponent implements OnInit, OnDestroy {
  private static readonly UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private readonly route = inject(ActivatedRoute);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly agentApi = inject(AgentApiService);
  private readonly billingApi = inject(BillingApiService);
  private readonly destroy$ = new Subject<void>();

  tenantUuid = '';
  loading = true;
  agentsLoading = false;
  loadError = '';
  client: TenantDetailPayload | null = null;
  agents: AgentRecord[] = [];
  agentsTotal = 0;

  billingType: 'PREPAID' | 'POSTPAID' = 'POSTPAID';
  billingFrequency = '';
  outstandingBalance = 0;
  /** Raw `{ slug: rate }` map from the tenant payload; paired with `productsCatalog` in the `productCharges` getter so display resolves correctly regardless of which arrives first. */
  private productChargesRaw: Record<string, number> = {};
  productsCatalog: CatalogRow[] = [];

  get isPostpaid(): boolean {
    return this.billingType === 'POSTPAID';
  }

  private readonly frequencyLabels: Record<string, string> = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
  };

  get billingFrequencyLabel(): string {
    return this.frequencyLabels[this.billingFrequency] ?? '—';
  }

  get productCharges(): Array<{ name: string; amount: number }> {
    return Object.entries(this.productChargesRaw).map(([slug, amount]) => ({
      name: this.productsCatalog.find((p) => p.slug === slug)?.name ?? slug,
      amount: Number(amount),
    }));
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const uuid = (params.get('uuid') ?? '').trim();
      if (!ClientDetailComponent.UUID_REGEX.test(uuid)) {
        this.loadError = 'Invalid client link.';
        this.loading = false;
        return;
      }
      this.tenantUuid = uuid;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.client = null;
    this.agents = [];
    this.agentsTotal = 0;
    this.outstandingBalance = 0;
    this.productChargesRaw = {};

    this.tenantApi
      .getTenantByUuid(this.tenantUuid)
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          const tenant = env.data;
          if (!tenant?.id) {
            this.loadError = 'Client not found.';
            this.loading = false;
            return;
          }
          this.client = tenant;
          this.loading = false;
          this.loadAgents(tenant.id);
          this.loadBilling(tenant);
          // Catalog only feeds the per-PD product charge names shown for POSTPAID tenants (template gates on isPostpaid).
          if (tenant.billing_type === 'POSTPAID') {
            this.loadProductsCatalog();
          }
        },
        error: (err) => {
          this.loading = false;
          this.loadError = resolveApiErrorMessage(err) || 'Could not load client details.';
        },
      });
  }

  private loadProductsCatalog(): void {
    this.tenantApi
      .listProductsDropdown()
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          this.productsCatalog = env.data ?? [];
        },
        error: () => {
          this.productsCatalog = [];
        },
      });
  }

  private loadBilling(tenant: TenantDetailPayload): void {
    this.billingType = tenant.billing_type ?? 'POSTPAID';
    this.billingFrequency = tenant.billing_frequency ?? '';
    this.productChargesRaw = tenant.product_charges ?? {};
    this.outstandingBalance = 0;

    if (!this.isPostpaid) {
      return;
    }

    this.billingApi
      .tenantSummary(tenant.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.outstandingBalance = res.data.pending_total;
        },
        error: () => {
          this.outstandingBalance = 0;
        },
      });
  }

  private loadAgents(tenantId: number): void {
    this.agentsLoading = true;
    this.agentApi
      .listAgents({ tenant_id: tenantId, page: 1, page_size: 100 })
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.agentsLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (env) => {
          this.agents = env.data?.items ?? [];
          this.agentsTotal = env.data?.total ?? this.agents.length;
        },
        error: () => {
          this.agents = [];
          this.agentsTotal = 0;
        },
      });
  }

  billingAddressLine(client: { billing_address?: string | null; billing_city?: string | null; billing_pincode?: string | null }): string {
    const cityLine = [client.billing_city, client.billing_pincode].filter(Boolean).join(' - ');
    return [client.billing_address, cityLine].filter(Boolean).join(', ') || '—';
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
  }

  clientStatusBadgeClass(status: string | undefined = ''): Record<string, boolean> {
    const s = status;
    return {
      'text-success bg-success-light': s === 'Active',
      'text-secondary bg-secondary-light': s === 'InActive',
      'text-danger bg-danger-light': s === 'Blocked',
      'text-warning bg-warning-light': s === 'Suspended' || s === 'Locked',
    };
  }

  agentStatusBadgeClass(status: string | undefined): Record<string, boolean> {
    const s = (status ?? '').toUpperCase();
    return {
      'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50':
        s === 'ACTIVE' || s === 'DEPLOYED',
      'text-sky-700 bg-sky-100 dark:text-sky-300 dark:bg-sky-950/50': s === 'DRAFT',
      'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-white/5': s === 'INACTIVE',
    };
  }

  productName(agent: AgentRecord): string {
    return agent.product?.name ?? '—';
  }
}
